/**
 * Schema-driven advanced filter builder component.
 * Fetches filter schema from backend and provides appropriate value inputs.
 * Uses PropertyInput for value inputs, enabling code reuse with form inputs.
 */

import { PlusCircle } from '@untitledui/icons';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useFilter } from 'react-aria';

import { Button } from '@/lib/base/buttons/button';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { QueryErrorState } from '@/lib/components';
import type { AssociationType } from '@/lib/crm/core/SchemaClient';
import type { EntityTypeName } from '@/lib/crm/core/types';
import { MAX_QUERY_FILTER_GROUPS } from '@/lib/crm/filters/constants';
import type {
  FilterCondition,
  FilterConditionGroup,
  OperatorType,
} from '@/lib/crm/filters/types';
import { useAssociationMatchers } from '@/lib/crm/hooks/useAssociationMatchers';
import { useFilterSchema } from '@/lib/crm/hooks/useFilterSchema';
import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';
import { getEntityKinds } from '@/lib/crm/utils/associationMatching';
import type { CrmObject } from '@/types/crm';

import { GroupCard } from './SchemaFilterBuilder/GroupCard';
import {
  ASSOCIATION_OPERATOR_LABELS,
  type AssociationOperatorType,
  type GroupSummary,
  hasIncompleteFilterConditions,
  isConditionValueInvalid,
  nextFilterId,
} from './SchemaFilterBuilder/shared';
import { useAssociatedPropertySchemas } from './SchemaFilterBuilder/useAssociatedPropertySchemas';
import { useFilterFieldSections } from './SchemaFilterBuilder/useFilterFieldSections';

export type { FilterCondition, OperatorType };
export type { AssociationOperatorType };
// Re-exported for backward-compatible public API — consumers import these
// directly from '@/lib/crm/components/SchemaFilterBuilder'.
// eslint-disable-next-line react-refresh/only-export-components -- constant co-located with component
export { ASSOCIATION_OPERATOR_LABELS };
// eslint-disable-next-line react-refresh/only-export-components -- helper co-located with component
export { hasIncompleteFilterConditions };

interface SchemaFilterBuilderProps {
  orgId: string | number;
  entityType: EntityTypeName;
  groups: FilterConditionGroup[];
  onChange: (groups: FilterConditionGroup[]) => void;
  onApply: (conditions?: FilterCondition[]) => void;
  /**
   * Fires when a condition or group is removed so the consumer can re-query
   * immediately. Group-native: receives the full `FilterConditionGroup[]` so
   * OR structure is preserved (a flat conditions list would collapse OR→AND).
   * Single-group surfaces flatten at their own adapter boundary.
   */
  onAutoApply?: (groups: FilterConditionGroup[]) => void;
  /**
   * When true (default), renders OR-separated AND-groups with add/remove-group
   * and move-condition controls. When false, renders a single ungrouped list
   * of conditions matching the legacy flat-filter UI.
   */
  allowGroups?: boolean;
  /** Callback when filterable properties are loaded - exposes property labels for filter chips */
  onPropertiesLoaded?: (properties: PropertyTypeSchema[]) => void;
  /** Callback when filterable associations are loaded */
  onAssociationsLoaded?: (associations: AssociationType[]) => void;
  /** Callback when association objects are selected (entity_id -> CrmObject) */
  onAssociationObjectsChange?: (objects: Record<string, CrmObject>) => void;
  /**
   * Reports whether every condition's value is valid for its field type
   * (e.g. non-email strings on an email "equals" filter fail validity).
   * Parents use this to gate their own Apply button.
   */
  onValidityChange?: (isValid: boolean) => void;
  /**
   * When true, association properties are surfaced directly in the field
   * combobox (e.g. "Industry  Works At") so users can filter by a PROPERTY
   * of an associated target record in one step. Each association's record-
   * picker item is relabelled "Specific {type}" (or plural for to-many).
   *
   * Opt-in because only surfaces that serialize via
   * `convertConditionGroupsToFilterGroups` (CRM list, pipeline) understand the
   * `target_property` clause — the reports wizard uses a different (Cube.js)
   * query path and must not emit it.
   */
  enableAssociatedPropertyFilters?: boolean;
}

export default function SchemaFilterBuilder({
  orgId,
  entityType,
  groups,
  onChange,
  onApply,
  onAutoApply,
  onPropertiesLoaded,
  onAssociationsLoaded,
  onAssociationObjectsChange,
  onValidityChange,
  enableAssociatedPropertyFilters = false,
  allowGroups = true,
}: SchemaFilterBuilderProps) {
  const {
    filterablePropertyTypes,
    propertyTypeMap,
    fieldTypeMap,
    loading,
    error,
    retry,
  } = useFilterSchema(orgId, entityType);

  // Convert orgId to number for API hooks
  const teamId = typeof orgId === 'string' ? parseInt(orgId, 10) : orgId;

  // Fetch association types + kind-aware matchers
  const {
    associationTypes,
    isLoading: associationsLoading,
    error: associationsError,
    matchesSource,
    entityKindMap,
  } = useAssociationMatchers(teamId);

  // Filter associations for current object type with user-facing access.
  // entityType is already a entity_type_name (e.g., 'ahoy_contact'), so use it
  // directly — no lookup through objectTypes needed. The prior lookup caused a
  // race condition where associations were always empty until objectTypes loaded.
  //
  // `matchesSource` covers kind-based associations like ahoy_contact_to_note
  // (source_entity_kind_name=person), which a plain
  // `source_entity_type_name === entityType` check would silently drop.
  const filterableAssociations = useMemo(() => {
    if (!entityType) {
      return [];
    }
    return associationTypes
      .filter(assoc => {
        if (!matchesSource(assoc, entityType)) {
          return false;
        }
        if (assoc.value_read_access === 'all') {
          return true;
        }
        const isTransitivePipeline =
          assoc.transitive_via != null &&
          assoc.transitive_via.length > 0 &&
          getEntityKinds(assoc.target_entity_type_name, entityKindMap).includes(
            'pipeline'
          );
        return isTransitivePipeline;
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [associationTypes, entityType, matchesSource, entityKindMap]);

  // Create association type lookup map by association_type_name
  const associationTypeLookup = useMemo(() => {
    const map: Record<string, AssociationType> = {};
    for (const assoc of filterableAssociations) {
      map[assoc.association_type_name] = assoc;
    }
    return map;
  }, [filterableAssociations]);

  // Log association fetch errors but don't block the UI
  useEffect(() => {
    if (associationsError) {
      console.error(
        'Failed to load association types for filtering:',
        associationsError
      );
    }
  }, [associationsError]);

  // Notify parent when properties are loaded
  useEffect(() => {
    if (onPropertiesLoaded && filterablePropertyTypes.length > 0) {
      onPropertiesLoaded(filterablePropertyTypes);
    }
  }, [filterablePropertyTypes, onPropertiesLoaded]);

  // Notify parent when associations are loaded
  useEffect(() => {
    if (onAssociationsLoaded && filterableAssociations.length > 0) {
      onAssociationsLoaded(filterableAssociations);
    }
  }, [filterableAssociations, onAssociationsLoaded]);

  // ---------------------------------------------------------------------------
  // Target entity type resolution + property schemas (for flattened combobox)
  // ---------------------------------------------------------------------------

  const {
    isObjectTypesError,
    objectTypeLookup,
    resolvedTargetTypes,
    targetFilterableProps,
    targetFetchErrors,
    targetPropertyMaps,
  } = useAssociatedPropertySchemas(
    teamId,
    enableAssociatedPropertyFilters,
    filterableAssociations,
    fieldTypeMap
  );

  // ---------------------------------------------------------------------------
  // Standard builder state
  // ---------------------------------------------------------------------------

  // `onAutoApply` is group-native (see prop docs). `onApply` stays flat — it's
  // the Enter-key convenience for consumers that reason about "all conditions"
  // rather than group structure; group-native surfaces ignore its argument and
  // apply from their own draft groups instead. Flatten groups once here for
  // that flat path and for validity checks.
  const allConditions = useMemo(
    () => groups.flatMap(group => group.conditions),
    [groups]
  );

  const hasIncompleteConditions = useMemo(
    () => hasIncompleteFilterConditions(allConditions),
    [allConditions]
  );

  // Per-condition invalidity map, keyed by condition id. Used both for
  // rendering inline hints and for reporting overall validity to the parent.
  const invalidConditionIds = useMemo(() => {
    const invalid = new Set<string>();
    for (const condition of allConditions) {
      const property = condition.field
        ? propertyTypeMap[condition.field]
        : undefined;
      if (isConditionValueInvalid(condition, property)) {
        invalid.add(condition.id);
      }
    }
    return invalid;
  }, [allConditions, propertyTypeMap]);

  useEffect(() => {
    if (!onValidityChange) {
      return;
    }
    onValidityChange(invalidConditionIds.size === 0);
  }, [invalidConditionIds, onValidityChange]);

  // Handle Enter key to trigger filter application (same validation gate as
  // the footer Apply button — completeness + type-level validity).
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        if (
          !hasIncompleteFilterConditions(allConditions) &&
          invalidConditionIds.size === 0
        ) {
          onApply(allConditions);
        }
      }
    },
    [onApply, allConditions, invalidConditionIds]
  );

  // ---------------------------------------------------------------------------
  // Combobox sections — properties section + per-association sections
  // ---------------------------------------------------------------------------

  const fieldSections = useFilterFieldSections(
    filterablePropertyTypes,
    filterableAssociations,
    enableAssociatedPropertyFilters,
    resolvedTargetTypes,
    targetFilterableProps,
    objectTypeLookup,
    targetFetchErrors
  );

  const { contains } = useFilter({ sensitivity: 'base' });
  const fieldFilter = useCallback(
    (textValue: string, inputValue: string) =>
      inputValue === '' || contains(textValue, inputValue),
    [contains]
  );

  // Stable group summaries (id + display label) fed to every GroupCard's
  // "move condition" menu. Each card derives its own otherGroups via useMemo
  // from this shared array — building a fresh per-card array here instead
  // would defeat GroupCard's memoization.
  const groupSummaries = useMemo<GroupSummary[]>(
    () =>
      groups.map((group, index) => ({
        id: group.id,
        label: `Group ${index + 1}`,
      })),
    [groups]
  );

  const addGroupButtonRef = useRef<HTMLButtonElement | null>(null);
  // Focus target for removeConditionById — the root container is always
  // present in both grouped and flat (allowGroups=false) modes, unlike
  // addGroupButtonRef which only renders in grouped mode.
  const builderRootRef = useRef<HTMLDivElement | null>(null);

  const updateConditionById = useCallback(
    (id: string, updates: Partial<FilterCondition>) => {
      onChange(
        groups.map(group => ({
          ...group,
          conditions: group.conditions.map(condition =>
            condition.id === id ? { ...condition, ...updates } : condition
          ),
        }))
      );
    },
    [onChange, groups]
  );

  const removeConditionById = useCallback(
    (id: string) => {
      let next = groups.map(group => ({
        ...group,
        conditions: group.conditions.filter(condition => condition.id !== id),
      }));
      // Removing a group's last condition removes the group itself (grouped
      // mode, when other groups remain) — a hollow card with a lone "Add
      // filter" reads as broken. The last group always survives so the
      // builder keeps an anchor for "Add filter".
      if (allowGroups && next.length > 1) {
        const nonEmpty = next.filter(group => group.conditions.length > 0);
        next = nonEmpty.length > 0 ? nonEmpty : [next[0]];
      }
      onChange(next);
      if (onAutoApply) {
        onAutoApply(next);
      }
      // Direct focus to the builder root instead of letting it fall to
      // <body> when the removed row's own controls were focused.
      setTimeout(() => {
        builderRootRef.current?.focus();
      }, 0);
    },
    [onChange, groups, onAutoApply, allowGroups]
  );

  const addConditionToGroup = useCallback(
    (groupId: string) => {
      if (
        filterablePropertyTypes.length === 0 &&
        filterableAssociations.length === 0
      ) {
        return;
      }

      // Start the new condition empty — the user picks a field via the
      // searchable combobox. Operator and value inputs render once a field or
      // association has been chosen.
      const newCondition: FilterCondition = {
        id: nextFilterId('condition'),
        field: '',
        operator: 'equals',
        value: null,
      };
      onChange(
        groups.map(group =>
          group.id === groupId
            ? { ...group, conditions: [...group.conditions, newCondition] }
            : group
        )
      );
    },
    [
      filterablePropertyTypes.length,
      filterableAssociations.length,
      onChange,
      groups,
    ]
  );

  const addGroup = useCallback(() => {
    if (groups.length >= MAX_QUERY_FILTER_GROUPS) {
      return;
    }
    if (
      filterablePropertyTypes.length === 0 &&
      filterableAssociations.length === 0
    ) {
      return;
    }
    // Seed the new group with one blank condition — an empty group renders
    // as a hollow card whose only content is "Add filter", which reads as
    // broken. Matches the add-a-group behavior of Airtable/Notion builders.
    const newCondition: FilterCondition = {
      id: nextFilterId('condition'),
      field: '',
      operator: 'equals',
      value: null,
    };
    onChange([
      ...groups,
      { id: nextFilterId('group'), conditions: [newCondition] },
    ]);
  }, [
    onChange,
    groups,
    filterablePropertyTypes.length,
    filterableAssociations.length,
  ]);

  // Both modes seed their first group lazily: when the parent passes an empty
  // `groups` array, there is no group to hang an "Add filter" button off, so
  // the first add creates the group and its first condition in one step.
  const addFirstCondition = useCallback(() => {
    if (
      filterablePropertyTypes.length === 0 &&
      filterableAssociations.length === 0
    ) {
      return;
    }
    const newCondition: FilterCondition = {
      id: nextFilterId('condition'),
      field: '',
      operator: 'equals',
      value: null,
    };
    onChange([{ id: nextFilterId('group'), conditions: [newCondition] }]);
  }, [filterablePropertyTypes.length, filterableAssociations.length, onChange]);

  const removeGroup = useCallback(
    (groupId: string) => {
      const next = groups.filter(group => group.id !== groupId);
      onChange(next);
      if (onAutoApply) {
        onAutoApply(next);
      }
      // Direct focus to a surviving element instead of letting it fall to
      // <body> when the removed group's own controls were focused.
      setTimeout(() => {
        addGroupButtonRef.current?.focus();
      }, 0);
    },
    [onChange, groups, onAutoApply]
  );

  const moveConditionToGroup = useCallback(
    (conditionId: string, targetGroupId: string) => {
      const sourceGroup = groups.find(group =>
        group.conditions.some(condition => condition.id === conditionId)
      );
      const condition = sourceGroup?.conditions.find(c => c.id === conditionId);
      if (!sourceGroup || !condition || sourceGroup.id === targetGroupId) {
        return;
      }
      const next = groups
        .map(group => {
          if (group.id === sourceGroup.id) {
            return {
              ...group,
              conditions: group.conditions.filter(c => c.id !== conditionId),
            };
          }
          if (group.id === targetGroupId) {
            return { ...group, conditions: [...group.conditions, condition] };
          }
          return group;
        })
        // Moving a group's only condition out prunes the emptied group —
        // same hollow-card rule as removing a last condition. The target
        // group always survives (it holds the moved condition).
        .filter(group => group.conditions.length > 0);
      onChange(next);
    },
    [groups, onChange]
  );

  // Shared props passed to every GroupCard. Memoized so its reference stays
  // stable across renders that don't touch these values — otherwise a new
  // object every render would break GroupCard's React.memo.
  const groupCardCommonProps = useMemo(
    () => ({
      fieldSections,
      fieldFilter,
      propertyTypeMap,
      fieldTypeMap,
      associationTypeLookup,
      targetPropertyMaps,
      teamId,
      invalidConditionIds,
      onUpdateCondition: updateConditionById,
      onRemoveCondition: removeConditionById,
      onKeyDown: handleKeyDown,
      onAssociationObjectsChange,
      onAddCondition: addConditionToGroup,
      onRemoveGroup: removeGroup,
      onMoveCondition: moveConditionToGroup,
      groupSummaries,
      addFilterDisabled: hasIncompleteConditions,
    }),
    [
      fieldSections,
      fieldFilter,
      propertyTypeMap,
      fieldTypeMap,
      associationTypeLookup,
      targetPropertyMaps,
      teamId,
      invalidConditionIds,
      updateConditionById,
      removeConditionById,
      handleKeyDown,
      onAssociationObjectsChange,
      addConditionToGroup,
      removeGroup,
      moveConditionToGroup,
      groupSummaries,
      hasIncompleteConditions,
    ]
  );

  // Loading state - wait for properties, associations are optional
  if (loading || associationsLoading) {
    return (
      <div className='py-8 px-4 text-center'>
        <LoadingIndicator size='xs' />
        <p className='text-sm text-secondary'>Loading filter options…</p>
      </div>
    );
  }

  // Error state - only block for property errors, not association errors
  if (error) {
    return (
      <QueryErrorState
        error={error}
        label='Unable to load filter options'
        onRetry={retry}
      />
    );
  }

  // No properties or associations available
  if (
    filterablePropertyTypes.length === 0 &&
    filterableAssociations.length === 0
  ) {
    return (
      <div className='py-8 px-4 bg-bg-secondary rounded-lg border border-dashed border-secondary'>
        <p className='text-sm text-secondary text-center'>
          No filterable properties available
        </p>
      </div>
    );
  }

  let flatModeContent: React.ReactNode = null;
  if (!allowGroups) {
    flatModeContent = groups[0] ? (
      <>
        {allConditions.length === 0 && (
          <div className='text-center py-8 px-4 bg-bg-secondary rounded-lg border border-dashed border-secondary'>
            <p className='text-sm text-secondary mb-2'>No filters applied</p>
            <p className='text-xs text-quaternary'>
              Click "Add filter" below to create your first condition
            </p>
          </div>
        )}
        <GroupCard
          group={groups[0]}
          groupIndex={0}
          totalGroups={1}
          allowGroups={false}
          {...groupCardCommonProps}
        />
      </>
    ) : (
      // No group exists yet — render an empty state with its own add action so
      // flat mode never becomes a dead end when `groups` is empty.
      <div className='text-center py-8 px-4 bg-bg-secondary rounded-lg border border-dashed border-secondary'>
        <p className='text-sm text-secondary mb-2'>No filters applied</p>
        <p className='text-xs text-quaternary mb-3'>
          Add a condition to start filtering
        </p>
        <Button
          color='tertiary'
          size='sm'
          iconLeading={PlusCircle}
          onClick={addFirstCondition}
        >
          Add filter
        </Button>
      </div>
    );
  }

  return (
    <div
      ref={builderRootRef}
      tabIndex={-1}
      className='space-y-2.5 focus:outline-none'
    >
      {isObjectTypesError && enableAssociatedPropertyFilters && (
        <p className='px-3 py-1 text-xs text-error'>
          Couldn&apos;t load property details for associated records. Try
          refreshing.
        </p>
      )}
      {/* Conditions List */}
      {!allowGroups ? (
        flatModeContent
      ) : (
        <>
          {/* No group exists yet — mirror flat mode's empty state so grouped
              mode never opens on a bare "Add group" with no way to add a
              condition directly. `groups` is empty here, so the map below
              renders nothing. */}
          {groups.length === 0 && (
            <div className='text-center py-8 px-4 bg-bg-secondary rounded-lg border border-dashed border-secondary'>
              <p className='text-sm text-secondary mb-2'>No filters applied</p>
              <p className='text-xs text-quaternary mb-3'>
                Add a condition to start filtering
              </p>
              <Button
                color='tertiary'
                size='sm'
                iconLeading={PlusCircle}
                onClick={addFirstCondition}
              >
                Add filter
              </Button>
            </div>
          )}
          {groups.map((group, index) => (
            <React.Fragment key={group.id}>
              {index > 0 && (
                <div
                  role='separator'
                  aria-label='or'
                  data-component='filter-group-or'
                  className='flex items-center gap-3 text-xs font-semibold text-quaternary uppercase tracking-wide'
                >
                  <span className='h-px flex-1 bg-border-secondary' />
                  OR
                  <span className='h-px flex-1 bg-border-secondary' />
                </div>
              )}
              <GroupCard
                group={group}
                groupIndex={index}
                totalGroups={groups.length}
                allowGroups
                {...groupCardCommonProps}
              />
            </React.Fragment>
          ))}

          <div className='flex items-center gap-2'>
            <Button
              ref={addGroupButtonRef}
              color='tertiary'
              size='sm'
              iconLeading={PlusCircle}
              onClick={addGroup}
              // Shares the Add filter gate: a new group starts with a blank
              // condition, so spawning one while another blank condition
              // exists would sidestep the complete-one-first rule.
              isDisabled={
                groups.length >= MAX_QUERY_FILTER_GROUPS ||
                hasIncompleteConditions
              }
              aria-describedby={
                groups.length >= MAX_QUERY_FILTER_GROUPS
                  ? 'group-cap-hint'
                  : undefined
              }
              data-component='add-filter-group'
            >
              Add group
            </Button>
            <div aria-live='polite'>
              {groups.length >= MAX_QUERY_FILTER_GROUPS && (
                <p id='group-cap-hint' className='text-xs text-tertiary'>
                  Maximum of {MAX_QUERY_FILTER_GROUPS} groups
                </p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
